/**
 * ESG Security Evaluation Component
 * 
 * Comprehensive security evaluation dashboard implementing industry-standard security frameworks.
 * Provides detailed analysis of security posture, vulnerability assessment, and compliance monitoring.
 * 
 * Features:
 * - Security Score Overview with multi-dimensional scoring across 10 domains
 * - Domain Analysis covering IAM, Network, Data Protection, Application Security, etc.
 * - Vulnerability Assessment with CVSS scoring and remediation tracking
 * - Penetration Testing results and methodology tracking
 * - Compliance Status for ISO 27001, SOC 2, NIST CSF, CIS Controls, PCI DSS
 * - Threat Intelligence with IOCs and attack trend analysis
 * - Incident Response metrics and team performance
 * - Security Governance structure and policy management
 * - Security Awareness training and culture metrics
 * - Continuous Monitoring coverage and automation status
 * - Industry Benchmarking and peer comparison
 * - Recommendations for security enhancement
 * - Trend Analysis and emerging threat landscape
 * 
 * @version 1.0.0
 * @created 2025-09-23
 */

import React, { useState, useEffect } from 'react';
import esgSecurityEvaluationService from '../../../services/esg/esgSecurityEvaluationService';

const ESGSecurityEvaluation = () => {
  const [securityData, setSecurityData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Initial data load
    const loadSecurityData = () => {
      try {
        const data = esgSecurityEvaluationService.getSecurityEvaluation();
        setSecurityData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading security evaluation data:', error);
        setLoading(false);
      }
    };

    loadSecurityData();

    // Subscribe to updates
    const unsubscribe = esgSecurityEvaluationService.subscribe((updatedData) => {
      setSecurityData(updatedData);
    });

    return () => unsubscribe();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await esgSecurityEvaluationService.refreshSecurityData();
    } catch (error) {
      console.error('Error refreshing security data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#22c55e'; // Brighter Green for better contrast
    if (score >= 80) return '#fbbf24'; // Brighter Yellow
    if (score >= 70) return '#f87171'; // Brighter Red
    return '#ef4444'; // Bright Red
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low': return '#4ade80'; // Bright Green
      case 'medium': return '#fbbf24'; // Bright Yellow
      case 'high': return '#fb923c'; // Bright Orange
      case 'critical': return '#f87171'; // Bright Red
      default: return '#9ca3af'; // Light Gray
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#f87171'; // Bright Red
      case 'high': return '#fb923c'; // Bright Orange
      case 'medium': return '#fbbf24'; // Bright Yellow
      case 'low': return '#4ade80'; // Bright Green
      case 'informational': return '#60a5fa'; // Bright Blue
      default: return '#9ca3af'; // Light Gray
    }
  };

  const getComplianceColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'compliant': return '#4ade80'; // Bright Green
      case 'substantially compliant': return '#fbbf24'; // Bright Yellow
      case 'non-compliant': return '#f87171'; // Bright Red
      default: return '#9ca3af'; // Light Gray
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(59, 130, 246, 0.3)',
            borderTop: '6px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 30px'
          }}></div>
          <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '10px' }}>🔒 Loading Security Evaluation</h3>
          <p style={{ color: '#94a3b8' }}>Analyzing security posture and compliance...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }}`}</style>
        </div>
      </div>
    );
  }

  if (!securityData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          padding: '50px',
          textAlign: 'center',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#fecaca', fontSize: '28px', marginBottom: '15px', fontWeight: '700' }}>Error Loading Security Evaluation</h2>
          <p style={{ color: '#cbd5e1', marginBottom: '30px' }}>Unable to load security evaluation data. Please try again.</p>
          <button 
            onClick={handleRefresh}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px', marginBottom: '30px' }}>
        {/* Security Score Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          borderRadius: '24px',
          padding: '35px',
          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, background: 'rgba(255, 255, 255, 0.05)', borderRadius: '50%' }}></div>
          <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '700', marginBottom: '25px', position: 'relative' }}>🛡️ Overall Security Score</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '25px', position: 'relative' }}>
            <div style={{ 
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: `conic-gradient(${getScoreColor(securityData.overview.overallScore)} ${securityData.overview.overallScore * 3.6}deg, rgba(255, 255, 255, 0.1) 0deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '56px', fontWeight: '800', color: 'white', lineHeight: 1 }}>{securityData.overview.overallScore}</span>
                <span style={{ fontSize: '20px', fontWeight: '600', color: '#bfdbfe', marginTop: '5px' }}>{securityData.overview.grade}</span>
              </div>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e0f2fe', fontSize: '14px', fontWeight: '600' }}>Risk Level:</span>
              <span style={{ color: getRiskColor(securityData.overview.riskLevel), fontSize: '16px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{securityData.overview.riskLevel}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e0f2fe', fontSize: '14px', fontWeight: '600' }}>Last Evaluation:</span>
              <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '700' }}>{new Date(securityData.overview.lastEvaluation).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
              <span style={{ color: '#e0f2fe', fontSize: '14px', fontWeight: '600' }}>Next Assessment:</span>
              <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '700' }}>{new Date(securityData.overview.nextAssessment).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        {/* Compliance Status Card */}
        <div style={{
          background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
          borderRadius: '24px',
          padding: '35px',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, background: 'rgba(255, 255, 255, 0.05)', borderRadius: '50%' }}></div>
          <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', position: 'relative', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>✅ Compliance Status</h3>
          <div style={{ display: 'grid', gap: '15px', position: 'relative' }}>
            {[
              { label: 'ISO 27001', status: securityData.overview.complianceStatus.iso27001 },
              { label: 'SOC 2', status: securityData.overview.complianceStatus.soc2 },
              { label: 'NIST CSF', status: securityData.overview.complianceStatus.nist },
              { label: 'PCI DSS', status: securityData.overview.complianceStatus.pciDss }
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '15px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid rgba(255, 255, 255, 0.25)'
              }}>
                <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '700' }}>{item.label}:</span>
                <span style={{ 
                  color: '#000000', 
                  fontSize: '13px', 
                  fontWeight: '800',
                  background: getComplianceColor(item.status),
                  padding: '7px 16px',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications Card */}
        <div style={{
          background: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)',
          borderRadius: '24px',
          padding: '35px',
          boxShadow: '0 8px 32px rgba(234, 88, 12, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, background: 'rgba(255, 255, 255, 0.05)', borderRadius: '50%' }}></div>
          <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', position: 'relative', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>🏆 Active Certifications</h3>
          <div style={{ display: 'grid', gap: '15px', position: 'relative' }}>
            {securityData.overview.certifications.map((cert, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '18px',
                border: '1px solid rgba(255, 255, 255, 0.25)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '800' }}>{cert.name}</span>
                  <span style={{
                    background: cert.status === 'Active' ? '#10b981' : '#fbbf24',
                    color: '#000000',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}>
                    {cert.status}
                  </span>
                </div>
                <div style={{ color: '#ffffff', fontSize: '13px', fontWeight: '700' }}>
                  ⏰ Expires: {new Date(cert.expires).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        {[
          {
            icon: '🔍',
            title: 'Total Vulnerabilities',
            value: securityData.vulnerabilities.summary.totalVulnerabilities,
            label: 'Found in Latest Scan',
            subtext: `${securityData.vulnerabilities.summary.critical} Critical, ${securityData.vulnerabilities.summary.high} High`,
            gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            shadowColor: 'rgba(239, 68, 68, 0.3)'
          },
          {
            icon: '🚨',
            title: 'Security Incidents',
            value: securityData.incidentResponse.summary.total2025,
            label: 'Total in 2025',
            subtext: `MTTR: ${securityData.incidentResponse.summary.mttr}`,
            gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
            shadowColor: 'rgba(245, 158, 11, 0.3)'
          },
          {
            icon: '📊',
            title: 'Monitoring Coverage',
            value: `${securityData.monitoring.coverage.endpoints}%`,
            label: 'Endpoints Protected',
            subtext: `${securityData.monitoring.tools.length} Security Tools Active`,
            gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            shadowColor: 'rgba(6, 182, 212, 0.3)'
          },
          {
            icon: '🎓',
            title: 'Security Training',
            value: `${securityData.awareness.training.generalSecurity.completion}%`,
            label: 'Completion Rate',
            subtext: `Phishing Click Rate: ${securityData.awareness.training.phishingSimulation.clickRate}%`,
            gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            shadowColor: 'rgba(168, 85, 247, 0.3)'
          }
        ].map((metric, idx) => (
          <div key={idx} style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '28px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = `0 12px 40px ${metric.shadowColor}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              background: metric.gradient,
              opacity: 0.1,
              borderRadius: '50%',
              filter: 'blur(20px)'
            }}></div>
            <div style={{
              background: metric.gradient,
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              marginBottom: '18px',
              boxShadow: `0 4px 15px ${metric.shadowColor}`
            }}>
              {metric.icon}
            </div>
            <h4 style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '700', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {metric.title}
            </h4>
            <div style={{ color: '#ffffff', fontSize: '36px', fontWeight: '900', marginBottom: '8px', lineHeight: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              {metric.value}
            </div>
            <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
              {metric.label}
            </div>
            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>
              {metric.subtext}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDomains = () => (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#ffffff', fontSize: '32px', fontWeight: '900', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🔐 Security Domain Analysis</h2>
        <p style={{ color: '#cbd5e1', fontSize: '16px', fontWeight: '500' }}>Detailed breakdown of security assessment across 10 critical domains</p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '25px' }}>
        {Object.entries(securityData.domains).map(([key, domain]) => (
          <div key={key} style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '28px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', margin: 0 }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </h3>
              <div style={{
                background: `linear-gradient(135deg, ${getScoreColor(domain.score)} 0%, ${getScoreColor(domain.score)}dd 100%)`,
                color: '#000000',
                padding: '8px 16px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '900',
                boxShadow: `0 4px 15px ${getScoreColor(domain.score)}40`
              }}>
                {domain.score}
              </div>
            </div>
            
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600' }}>Weight: {domain.weight}%</span>
                <span style={{ color: getScoreColor(domain.score), fontSize: '14px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{domain.status}</span>
              </div>
              <p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.6', margin: 0, fontWeight: '500' }}>{domain.description}</p>
            </div>
            
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              padding: '18px',
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <h4 style={{ color: '#ffffff', fontSize: '14px', fontWeight: '700', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Component Breakdown:
              </h4>
              {Object.entries(domain.details).map(([subKey, value]) => (
                <div key={subKey} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                }}>
                  <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                    {subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                  </span>
                  <span style={{
                    color: '#000000',
                    fontSize: '14px',
                    fontWeight: '800',
                    background: getScoreColor(value),
                    padding: '5px 12px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                  }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderVulnerabilities = () => (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#ffffff', fontSize: '32px', fontWeight: '900', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🔍 Vulnerability Assessment</h2>
        <p style={{ color: '#cbd5e1', fontSize: '16px', fontWeight: '500' }}>Comprehensive vulnerability analysis and remediation tracking</p>
      </div>
      
      {/* Vulnerability Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {[
          { severity: 'Critical', count: securityData.vulnerabilities.summary.critical, color: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' },
          { severity: 'High', count: securityData.vulnerabilities.summary.high, color: '#ea580c', gradient: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)' },
          { severity: 'Medium', count: securityData.vulnerabilities.summary.medium, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
          { severity: 'Low', count: securityData.vulnerabilities.summary.low, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
          { severity: 'Informational', count: securityData.vulnerabilities.summary.informational, color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }
        ].map((item, idx) => (
          <div key={idx} style={{
            background: item.gradient,
            borderRadius: '16px',
            padding: '24px',
            textAlign: 'center',
            boxShadow: `0 8px 25px ${item.color}40`,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = `0 12px 35px ${item.color}60`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `0 8px 25px ${item.color}40`;
          }}>
            <div style={{ fontSize: '42px', fontWeight: '900', color: '#ffffff', marginBottom: '8px', lineHeight: 1, textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
              {item.count}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.5px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {item.severity}
            </div>
          </div>
        ))}
      </div>

      {/* High Priority Vulnerabilities */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>⚠️ High Priority Vulnerabilities</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {securityData.vulnerabilities.highVulnerabilities.map((vuln) => (
            <div key={vuln.id} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              padding: '24px',
              border: `2px solid ${getSeverityColor(vuln.severity)}40`,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = `0 10px 30px ${getSeverityColor(vuln.severity)}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '800', margin: 0 }}>{vuln.id}</h4>
                <span style={{
                  background: getSeverityColor(vuln.severity),
                  color: '#000000',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                }}>
                  {vuln.severity}
                </span>
              </div>
              <div style={{ color: '#ffffff', fontSize: '15px', fontWeight: '700', marginBottom: '15px' }}>{vuln.title}</div>
              <div style={{ marginBottom: '15px' }}>
                {[
                  { label: 'CVSS Score', value: vuln.cvssScore, color: getSeverityColor(vuln.severity) },
                  { label: 'Affected', value: vuln.affected, color: '#cbd5e1' },
                  { label: 'Status', value: vuln.status, color: '#10b981' },
                  { label: 'Due Date', value: new Date(vuln.dueDate).toLocaleDateString(), color: '#cbd5e1' }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < 3 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none' }}>
                    <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600' }}>{item.label}:</span>
                    <span style={{ color: item.color, fontSize: '13px', fontWeight: '800', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <p style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.5', margin: 0, fontWeight: '500' }}>{vuln.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scan Coverage */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>📊 Scan Coverage</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          {Object.entries(securityData.vulnerabilities.scanCoverage).map(([key, value]) => (
            <div key={key} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '700' }}>
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </span>
                <span style={{ color: getScoreColor(value), fontSize: '16px', fontWeight: '900', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{value}%</span>
              </div>
              <div style={{ height: '8px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  width: `${value}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${getScoreColor(value)} 0%, ${getScoreColor(value)}dd 100%)`,
                  transition: 'width 0.5s ease',
                  boxShadow: `0 0 10px ${getScoreColor(value)}80`
                }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Remediation Metrics */}
      <div>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>⏱️ Remediation Metrics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {[
            { label: 'Average Time to Fix', value: securityData.vulnerabilities.remediationMetrics.averageTimeToFix, icon: '⏰', color: '#3b82f6' },
            { label: 'SLA Compliance', value: `${securityData.vulnerabilities.remediationMetrics.slaCompliance}%`, icon: '✓', color: getScoreColor(securityData.vulnerabilities.remediationMetrics.slaCompliance) },
            { label: 'High Priority Remediation', value: securityData.vulnerabilities.remediationMetrics.highRemediationTime, icon: '🔥', color: '#ef4444' },
            { label: 'Medium Priority Remediation', value: securityData.vulnerabilities.remediationMetrics.mediumRemediationTime, icon: '⚡', color: '#f59e0b' }
          ].map((metric, idx) => (
            <div key={idx} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = `0 8px 25px ${metric.color}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{
                background: `linear-gradient(135deg, ${metric.color} 0%, ${metric.color}dd 100%)`,
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                marginBottom: '15px',
                boxShadow: `0 4px 15px ${metric.color}40`
              }}>
                {metric.icon}
              </div>
              <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {metric.label}
              </div>
              <div style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                {metric.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPenetrationTesting = () => (
    <div style={{ padding: '30px' }}>
      <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🔓 Penetration Testing</h2>
      <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>External security assessment and penetration testing results</p>
      
      <div style={{ marginBottom: '30px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          borderRadius: '16px',
          padding: '30px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)'
        }}>
          <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>📋 Latest Assessment</h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>Test Date:</span>
              <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{new Date(securityData.penetrationTesting.summary.lastTest).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>Test Type:</span>
              <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{securityData.penetrationTesting.summary.testType}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>Duration:</span>
              <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{securityData.penetrationTesting.summary.duration}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
              <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>Tester:</span>
              <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{securityData.penetrationTesting.summary.tester}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
              <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>Overall Rating:</span>
              <span style={{
                color: '#000000',
                background: getRiskColor(securityData.penetrationTesting.summary.overallRating),
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '900',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                {securityData.penetrationTesting.summary.overallRating}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🔍 Key Findings</h3>
        <div style={{ display: 'grid', gap: '20px' }}>
          {securityData.penetrationTesting.findings.map((finding) => (
            <div key={finding.id} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              padding: '24px',
              border: `2px solid ${getRiskColor(finding.risk)}40`,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = `0 8px 32px ${getRiskColor(finding.risk)}30`;
              e.currentTarget.style.borderColor = getRiskColor(finding.risk);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = `${getRiskColor(finding.risk)}40`;
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', margin: 0, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{finding.id}</h4>
                <span style={{
                  background: getRiskColor(finding.risk),
                  color: '#000000',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '900',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  {finding.risk} Risk
                </span>
              </div>
              <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '700', marginBottom: '16px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{finding.title}</div>
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <p style={{ color: '#e2e8f0', fontSize: '14px', marginBottom: '12px', lineHeight: '1.6' }}>
                  <strong style={{ color: '#ffffff', fontWeight: '800' }}>Description:</strong> {finding.description}
                </p>
                <p style={{ color: '#e2e8f0', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                  <strong style={{ color: '#ffffff', fontWeight: '800' }}>Recommendation:</strong> {finding.recommendation}
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                <span style={{
                  background: finding.status === 'Resolved' ? '#22c55e' : finding.status === 'In Progress' ? '#fbbf24' : finding.status === 'Accepted Risk' ? '#60a5fa' : '#f87171',
                  color: '#000000',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '900',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  {finding.status}
                </span>
                {finding.resolvedDate && (
                  <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700' }}>
                    Resolved: {new Date(finding.resolvedDate).toLocaleDateString()}
                  </span>
                )}
                {finding.justification && (
                  <div style={{
                    width: '100%',
                    marginTop: '8px',
                    padding: '12px',
                    background: 'rgba(96, 165, 250, 0.15)',
                    borderLeft: '3px solid #60a5fa',
                    borderRadius: '8px'
                  }}>
                    <strong style={{ color: '#ffffff', fontWeight: '800', fontSize: '13px' }}>Justification:</strong>{' '}
                    <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>{finding.justification}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>🎯 Test Scope</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {Object.entries(securityData.penetrationTesting.testScope).map(([key, value]) => (
            <div key={key} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </div>
              <div style={{
                background: value === 'Included' || value === 'Full Coverage' || value === 'Complete' ? '#22c55e' : value === 'Partial' || value === 'Limited' ? '#fbbf24' : '#60a5fa',
                color: '#000000',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '900',
                display: 'inline-block',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '20px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>⚙️ Testing Methodology</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {securityData.penetrationTesting.methodology.map((method, index) => (
            <div key={index} style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '18px 24px',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
              e.currentTarget.style.transform = 'translateX(5px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}>
              <span style={{ 
                color: '#ffffff', 
                fontSize: '15px', 
                fontWeight: '700',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ color: '#60a5fa', fontSize: '18px' }}>✓</span>
                {method}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div style={{ padding: '30px' }}>
      <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>✓ Security Compliance</h2>
      <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>Comprehensive compliance assessment across security frameworks</p>
      
      <div style={{ display: 'grid', gap: '25px' }}>
        {Object.entries(securityData.complianceFrameworks).map(([key, framework]) => (
          <div key={key} style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>{framework.name}</h3>
              <div style={{
                background: `linear-gradient(135deg, ${getScoreColor(framework.score)} 0%, ${getScoreColor(framework.score)}dd 100%)`,
                color: '#000000',
                padding: '10px 20px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '900',
                boxShadow: `0 4px 15px ${getScoreColor(framework.score)}40`
              }}>
                {framework.score}/100
              </div>
            </div>
            <div style={{ 
              color: '#000000',
              background: getComplianceColor(framework.status),
              display: 'inline-block',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '900',
              marginBottom: '15px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              Status: {framework.status}
            </div>
            <div style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '600', marginBottom: '25px' }}>
              Last Audit: <span style={{ color: '#ffffff', fontWeight: '800' }}>{new Date(framework.lastAudit || framework.lastAssessment).toLocaleDateString()}</span>
            </div>
            
            <div>
              {framework.controls && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', marginBottom: '15px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Controls Implementation:</h4>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <span style={{ color: '#cbd5e1', fontSize: '15px', fontWeight: '700' }}>Implemented:</span>
                      <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '900', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{framework.controls.implemented}/{framework.controls.total}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                      <span style={{ color: '#cbd5e1', fontSize: '15px', fontWeight: '700' }}>Effectiveness:</span>
                      <span style={{
                        color: '#000000',
                        background: getScoreColor(framework.controls.effectiveness),
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '900',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                      }}>
                        {framework.controls.effectiveness}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {framework.trustServicesCriteria && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', marginBottom: '15px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Trust Services Criteria:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {Object.entries(framework.trustServicesCriteria).map(([criterion, score]) => (
                      <div key={criterion} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                      }}>
                        <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {criterion.charAt(0).toUpperCase() + criterion.slice(1)}:
                        </span>
                        <span style={{
                          color: '#000000',
                          background: getScoreColor(score),
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '900',
                          display: 'inline-block',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                          {score}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {framework.functions && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', marginBottom: '15px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Framework Functions:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {Object.entries(framework.functions).map(([func, score]) => (
                      <div key={func} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                      }}>
                        <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {func.charAt(0).toUpperCase() + func.slice(1)}:
                        </span>
                        <span style={{
                          color: '#000000',
                          background: getScoreColor(score),
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '900',
                          display: 'inline-block',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                          {score}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {framework.implementation && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', marginBottom: '15px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Implementation Levels:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {Object.entries(framework.implementation).map(([level, score]) => (
                      <div key={level} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                      }}>
                        <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {level.charAt(0).toUpperCase() + level.slice(1)}:
                        </span>
                        <span style={{
                          color: '#000000',
                          background: getScoreColor(score),
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '900',
                          display: 'inline-block',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                          {score}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {framework.requirements && (
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', marginBottom: '15px', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Requirements Status:</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {Object.entries(framework.requirements).map(([req, score]) => (
                      <div key={req} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                      }}>
                        <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {req.charAt(0).toUpperCase() + req.slice(1)}:
                        </span>
                        <span style={{
                          color: '#000000',
                          background: getScoreColor(score),
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '900',
                          display: 'inline-block',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                        }}>
                          {score}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: '20px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Findings</div>
                  <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>{framework.findings || 'N/A'}</div>
                </div>
                <div style={{ width: '1px', background: 'rgba(255, 255, 255, 0.15)' }}></div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Non-Conformities</div>
                  <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>{framework.nonConformities || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Security Overview', component: renderOverview },
    { id: 'domains', label: 'Security Domains', component: renderDomains },
    { id: 'vulnerabilities', label: 'Vulnerabilities', component: renderVulnerabilities },
    { id: 'pentesting', label: 'Penetration Testing', component: renderPenetrationTesting },
    { id: 'compliance', label: 'Compliance', component: renderCompliance }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '0'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
        color: 'white',
        padding: '50px 30px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.4
        }}></div>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  padding: '15px',
                  borderRadius: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <span style={{ fontSize: '40px' }}>🔒</span>
                </div>
                <div>
                  <h1 style={{ fontSize: '42px', fontWeight: '800', margin: 0, textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)' }}>
                    Security Evaluation
                  </h1>
                  <p style={{ fontSize: '18px', margin: '5px 0 0 0', color: '#bfdbfe', fontWeight: '500' }}>
                    Comprehensive Security Assessment & Compliance Monitoring
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <button 
                onClick={handleRefresh} 
                disabled={refreshing}
                style={{
                  background: refreshing ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: refreshing ? 0.6 : 1
                }}
                onMouseEnter={(e) => !refreshing && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
              >
                <span style={{ fontSize: '18px' }}>{refreshing ? '⟳' : '🔄'}</span>
                {refreshing ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '0 30px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '5px', overflowX: 'auto' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: activeTab === tab.id ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#94a3b8',
                border: 'none',
                padding: '16px 28px',
                fontSize: '15px',
                fontWeight: activeTab === tab.id ? '700' : '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderBottom: activeTab === tab.id ? '3px solid #60a5fa' : '3px solid transparent',
                whiteSpace: 'nowrap',
                borderRadius: activeTab === tab.id ? '8px 8px 0 0' : '0'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.color = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px' }}>
        {tabs.find(tab => tab.id === activeTab)?.component()}
      </div>
    </div>
  );
};

export default ESGSecurityEvaluation;